// Core imports
import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Space, Col, Modal, Row, Typography, Divider, Grid, Tabs, Card } from 'antd';

// Misc imports
import { INTEGRATION_CATEGORY, INTEGRATIONS } from '../../constants/integrations';

// Constant declarations
const { useBreakpoint } = Grid;

// If any of this below changes, the selectors in integrations.less
// needs to change too
// Special Tabs to avoid alignment issues between tabBar and tabContent containers
const SPECIAL_TAB_TYPES = {
  SCOPE: 'SCOPE',
  ALL: 'ALL',
  DIVIDER: 'DIVIDER'
};

const CreateIntegrationModal = ({
  isModalOpen,
  closeModal,
  setIntegration
}) => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const isMobileSize = (screens.xs || screens.sm || screens.md) && !screens.lg;

  const handleCloseModal = () => {
    closeModal();
  };

  const handleSelectIntegration = (integration) => {
    setIntegration({ type: integration.type, category: integration.category });
    handleCloseModal();
  };

  const renderIntegrationContent = (categoryKey) => {
    const integrationsToShow = Object.values(INTEGRATIONS).filter((integration) => {
      if (integration.hidden) {
        return false;
      }
      if (categoryKey === SPECIAL_TAB_TYPES.ALL) {
        return true;
      }
      return integration.category === categoryKey;
    });

    return (
      <Row gutter={[18, 18]} className={`py-3 h-100 ${isMobileSize ? 'justify-content-center' : ''}`}>
        {integrationsToShow.map((integration) => (
          <Col
            key={integration.type}
            flex="0 0 280px"
          >
            <Card
              onClick={() => {
                handleSelectIntegration(integration);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelectIntegration(integration);
                }
              }}
              className="p-0 h-100 border-color-transparent cursor-pointer shadow-soft"
            >
              <Row gutter={[12, 12]} align="middle" justify="center" className="mx-auto w-fc text-center mt-5">
                <Col span={24}>
                  <Typography.Text className="fs-sm">
                    {t(integration.category, { context: 'integration category' })}
                  </Typography.Text>
                </Col>
                <Col span={24} className="pt-8">
                  {integration.renderTypeIcon({ className: 'fs-xxxl' })}
                </Col>
                <Col span={24} className="pb-8">
                  <Typography.Text className="fs-lg" strong>
                    {t(integration.type, { context: 'type' })}
                  </Typography.Text>
                </Col>
                <Col className="px-5 fs-base pb-8" span={24}>
                  <Typography.Paragraph ellipsis={{ rows: 2, tooltip: t(integration.type, { context: 'type description' }) }}>
                    {t(integration.type, { context: 'type description' })}
                  </Typography.Paragraph>
                </Col>
              </Row>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <Modal
      open={isModalOpen}
      className={`${isMobileSize ? 'w-80' : 'w-60'} ant-modal-p-0 integrations-modal`}
      centered
      onCancel={handleCloseModal}
      destroyOnClose
      footer={null}
      title={
        <Fragment>
          <Typography.Title level={4} className="mb-1 fw-800">
            <Space size={8} className="align-items-center">
              {t('Create New Integration')}
            </Space>
          </Typography.Title>
        </Fragment>
      }
    >
      <Row>
        <Col xs={24} className="p-4">
          <Tabs
            className={isMobileSize ? 'integrations-tab-mobile' : ''}
            tabPosition={isMobileSize ? 'top' : 'left'}
            destroyInactiveTabPane
            defaultActiveKey={SPECIAL_TAB_TYPES.ALL}
            items={[
              SPECIAL_TAB_TYPES.SCOPE,
              SPECIAL_TAB_TYPES.ALL,
              ...Object.values(INTEGRATION_CATEGORY).filter(category =>
                Object.values(INTEGRATIONS).some(integration => integration.category === category && !integration.hidden)
              ),
              SPECIAL_TAB_TYPES.DIVIDER
            ].map((category) => {

              if (category === SPECIAL_TAB_TYPES.SCOPE) {
                return {
                  label: (
                    <Space className="d-flex justify-content-between fs-base pointer-cursor-none">
                      <Typography.Text className="fw-400 text-muted fs-sm">
                        {t(category, { context: 'integration category' })}
                      </Typography.Text>
                    </Space>
                  ),
                  key: category,
                  disabled: true,
                  className: 'scope-tab',
                  children: null
                };
              }

              if (category === SPECIAL_TAB_TYPES.DIVIDER) {
                return {
                  label: <Divider className="mt-1 gradient-divider" />,
                  key: category,
                  disabled: true,
                  className: 'divider-tab',
                  children: null
                };
              }

              return {
                label: (
                  <Space className="d-flex justify-content-between fs-base">
                    <Typography.Text className="fw-500">
                      {t(category, { context: 'integration category' })}
                    </Typography.Text>
                    <Typography.Text className="text-muted">
                      {Object.values(INTEGRATIONS).filter((integration) =>
                        category === SPECIAL_TAB_TYPES.ALL ? !integration.hidden : integration.category === category && !integration.hidden
                      ).length}
                    </Typography.Text>
                  </Space>
                ),
                key: category,
                children: renderIntegrationContent(category)
              };
            })}
          />
        </Col>
      </Row>
    </Modal>
  );
};

CreateIntegrationModal.propTypes = {
  isModalOpen: PropTypes.bool,
  closeModal: PropTypes.func,
  setIntegration: PropTypes.func
};
export default CreateIntegrationModal;
